<template>
  <main class="settings-page">
    <!-- Header Card -->
        <PageHeader icon="fas fa-sliders-h">
      <template #title>
        Pengaturan Klinik
      </template>
      <template #subtitle>
        Kelola identitas klinik, informasi apotek, dan tampilan aplikasi
      </template>
    </PageHeader>

    <div class="settings-info-strip">
      <span><i class="fas fa-info-circle"></i></span>
      <p>Perubahan logo dan informasi aplikasi akan tampil setelah konfigurasi berhasil disimpan.</p>
    </div>

    <Card card-class="storage-card">
      <template v-slot:body>
        <div class="storage-card__heading">
          <div>
            <span class="settings-eyebrow">Kapasitas tenant</span>
            <h2>Penyimpanan data</h2>
            <p>Gabungan database dan seluruh file dari semua cabang dalam tenant ini.</p>
          </div>
          <button class="btn btn-sm btn-light-primary" :disabled="storageLoading" @click="loadStorage(true)">
            <i class="fas fa-sync-alt" :class="{'fa-spin':storageLoading}"></i> Perbarui
          </button>
        </div>
        <div v-if="storage" class="storage-card__content">
          <div class="storage-card__summary">
            <strong>{{ storage.used_human }}</strong>
            <span>dari {{ storage.limit_human }}</span>
            <b-badge v-if="storage.measurement_status!=='complete'" variant="warning">Data sementara</b-badge>
          </div>
          <div class="storage-progress" role="progressbar" :aria-valuenow="storage.usage_percent||0" aria-valuemin="0" aria-valuemax="100">
            <i :class="storageBarClass" :style="{width:(storage.usage_percent||0)+'%'}"></i>
          </div>
          <div class="storage-card__breakdown">
            <span><i class="fas fa-database"></i> Database <b>{{ storage.database_human }}</b></span>
            <span><i class="fas fa-folder-open"></i> File <b>{{ storage.object_human }}</b></span>
            <span><i class="fas fa-file"></i> {{ storage.object_count }} objek</span>
            <small>Diukur {{ storageMeasuredAt }}</small>
          </div>
          <p v-if="storage.usage_percent>=90" class="storage-warning"><i class="fas fa-exclamation-triangle"></i> Kapasitas hampir penuh. Upload baru akan ditolak ketika melewati batas paket.</p>
        </div>
        <div v-else-if="storageLoading" class="settings-loading"><span class="spinner-border spinner-border-sm"></span> Mengukur pemakaian storage...</div>
        <div v-else class="storage-error"><i class="fas fa-exclamation-circle"></i> {{ storageError }}</div>
      </template>
    </Card>

    <div class="row">
      <div class="col-md-12">
        <Card card-class="settings-form-shell">
          <template v-slot:body>
            <div class="settings-form-heading">
              <div>
                <span class="settings-eyebrow">Konfigurasi utama</span>
                <h2>Informasi dan preferensi</h2>
                <p>Pastikan informasi yang ditampilkan sudah sesuai dengan operasional klinik.</p>
              </div>
              <span class="settings-form-heading__icon"><i class="fas fa-cog"></i></span>
            </div>

            <Form v-if="dataLoaded" :form="form" :route="'settings/1'" />
            <div v-else class="settings-loading" role="status">
              <span class="spinner-border spinner-border-sm"></span>
              Memuat konfigurasi...
            </div>
          </template>
        </Card>
      </div>
    </div>
  </main>
</template>

<script>

import Card from "@/view/content/Card.vue"
import Form from '@/component/settings/Form.vue'
import module from '@/core/modules/CrudModule.js'
import ApiService from '@/core/services/api.service.js'
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module"
// import { setting, getSetting } from '@/core/services/jwt.service.js'

export default {

  components: {
    Card,
    Form
  },

  data() {
    return {
      // Form
      form: {},
      // Other
      dataLoaded: false,
      storage: null,
      storageLoading: false,
      storageError: ''
    }
  },

  methods: {

    async get() {
    //   this.form = await module.get('settings/' + this.$route.params.id)
      this.form = await module.get('settings/1')
      // If Data Not Found
      if (this.form == null) {
        // Redirect To List
        // this.$router.push('/settings/list')
      } else {
        this.form['_method'] = 'put'
        this.dataLoaded = true
      }
    },

    async loadStorage(refresh = false) {
      this.storageLoading = true
      this.storageError = ''
      try {
        const response = await ApiService.query('product-configuration/storage-usage', { params: { refresh: refresh ? 1 : 0 } })
        this.storage = response.data.data
      } catch (error) {
        const data = error && error.response && error.response.data
        this.storageError = (data && (data.message || data.error)) || 'Pemakaian storage belum dapat diukur.'
      } finally {
        this.storageLoading = false
      }
    }

  },

  mounted() {
    // BreadCrumb
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Konfigurasi", route: "" },
      { title: "Edit" },
    ])
    // Get Data
    this.get()
    this.loadStorage()
  },

  computed: {
    storageBarClass() {
      const usage = this.storage && this.storage.usage_percent || 0
      return usage >= 90 ? 'danger' : usage >= 75 ? 'warning' : ''
    },
    storageMeasuredAt() {
      if (!this.storage || !this.storage.measured_at) return '-'
      return new Intl.DateTimeFormat('id-ID', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(this.storage.measured_at))
    }
  }

}

</script>

<style lang="scss">
.settings-page {
  --settings-navy: #143263;
  --settings-blue: #14608f;
  --settings-teal: #1e958f;
  --settings-border: #e3eaf2;
  color: #1a2433;
}

.settings-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 12px;
  padding: 17px 19px;
  border: 1px solid var(--settings-border);
  border-radius: 17px;
  background: linear-gradient(110deg, rgba(20, 50, 99, 0.08), rgba(30, 149, 143, 0.07));
  box-shadow: 0 9px 24px rgba(20, 50, 99, 0.055);
}

.settings-hero__content {
  display: flex;
  align-items: center;
  gap: 13px;
}

.settings-hero__icon {
  display: grid;
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  place-items: center;
  color: #fff;
  border-radius: 13px;
  background: linear-gradient(135deg, var(--settings-navy), var(--settings-blue), var(--settings-teal));
  box-shadow: 0 7px 16px rgba(20, 96, 143, 0.18);
}

.settings-eyebrow {
  display: block;
  margin-bottom: 3px;
  color: var(--settings-teal);
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.settings-hero h1 {
  margin: 0 0 3px;
  color: #1a2433 !important;
  font-size: 21px !important;
  font-weight: 700;
}

.settings-hero p,
.settings-form-heading p {
  margin: 0;
  color: #6b7280;
  font-size: 11px;
}

.settings-hero__badge {
  display: inline-flex;
  min-height: 33px;
  padding: 7px 10px;
  align-items: center;
  gap: 7px;
  color: var(--settings-teal);
  border: 1px solid rgba(30, 149, 143, 0.18);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.78);
  font-size: 9px;
  font-weight: 700;
  white-space: nowrap;
}

.settings-info-strip {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 12px;
  padding: 9px 12px;
  color: #536174;
  border: 1px solid #dce7f1;
  border-radius: 11px;
  background: #f6f9fc;
}

.settings-info-strip > span { color: var(--settings-blue); }
.settings-info-strip p { margin: 0; font-size: 9px; }

.settings-page .storage-card.card { margin-bottom: 12px; border-radius: 17px !important; transform: none !important; }
.settings-page .storage-card > .card-body { padding: 17px !important; }
.storage-card__heading { display:flex; align-items:flex-start; justify-content:space-between; gap:14px; }
.storage-card__heading h2 { margin:0 0 3px; color:#1a2433 !important; font-size:16px !important; font-weight:700; }
.storage-card__heading p { margin:0; color:#6b7280; font-size:11px; }
.storage-card__content { margin-top:14px; }
.storage-card__summary { display:flex; align-items:baseline; gap:7px; }
.storage-card__summary strong { color:var(--settings-navy); font-size:21px; }
.storage-card__summary span { color:#6b7280; font-size:11px; }
.storage-progress { height:10px; margin:9px 0 11px; overflow:hidden; border-radius:999px; background:#e8eef4; }
.storage-progress i { display:block; min-width:2px; height:100%; border-radius:inherit; background:linear-gradient(90deg,var(--settings-blue),var(--settings-teal)); transition:width .3s ease; }
.storage-progress i.warning { background:#f59e0b; }
.storage-progress i.danger { background:#dc3545; }
.storage-card__breakdown { display:flex; align-items:center; flex-wrap:wrap; gap:13px; color:#657184; font-size:10px; }
.storage-card__breakdown span { display:inline-flex; align-items:center; gap:5px; }
.storage-card__breakdown i { color:var(--settings-blue); }
.storage-card__breakdown small { margin-left:auto; }
.storage-warning,.storage-error { margin:11px 0 0; padding:9px 11px; border-radius:9px; font-size:10px; }
.storage-warning { color:#92400e; background:#fffbeb; }
.storage-error { color:#9f1239; background:#fff1f2; }

.settings-page .settings-form-shell.card {
  border-radius: 17px !important;
  transform: none !important;
}

.settings-page .settings-form-shell > .card-body { padding: 17px !important; }

.settings-form-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 15px;
  padding-bottom: 13px;
  border-bottom: 1px solid #edf1f5;
}

.settings-form-heading h2 {
  margin: 0 0 3px;
  color: #1a2433 !important;
  font-size: 16px !important;
  font-weight: 700;
}

.settings-form-heading__icon {
  display: grid;
  width: 35px;
  height: 35px;
  place-items: center;
  color: var(--settings-blue);
  border-radius: 10px;
  background: rgba(20, 96, 143, 0.08);
}

.settings-loading {
  display: flex;
  min-height: 130px;
  align-items: center;
  justify-content: center;
  gap: 9px;
  color: #6b7280;
  font-size: 11px;
}

.settings-page .settings-form > form > .form-group:first-child {
  margin-bottom: 13px;
  padding: 13px;
  border: 1px solid var(--settings-border);
  border-radius: 13px;
  background: #fbfcfe;
}

.settings-page .settings-form fieldset {
  margin: 0 !important;
  padding: 15px !important;
  border: 1px solid var(--settings-border) !important;
  border-radius: 14px !important;
  background: #fff;
}

.settings-page .settings-form fieldset > legend {
  width: auto !important;
  margin: 0 0 12px !important;
  padding: 6px 10px !important;
  color: var(--settings-navy) !important;
  border: 1px solid rgba(20, 96, 143, 0.14) !important;
  border-radius: 9px !important;
  background: rgba(20, 96, 143, 0.07) !important;
  font-size: 11px !important;
  font-weight: 700;
}

.settings-page .settings-form .row.mt-7,
.settings-page .settings-form .row[class*="mt-7"] { margin-top: 13px !important; }

.settings-page .settings-form .form-group { margin-bottom: 12px; }

.settings-page .settings-form .form-group label,
.settings-page .settings-form .col-form-label,
.settings-page .settings-form fieldset > .row > [class*="col-"] > label {
  margin-bottom: 5px;
  color: #40536b !important;
  font-size: 10px !important;
  font-weight: 700 !important;
}

.settings-page .settings-form .form-control,
.settings-page .settings-form .custom-select {
  height: 40px !important;
  min-height: 40px !important;
  padding: 8px 11px !important;
  border-color: #dbe4ee !important;
  border-radius: 10px !important;
  background: #fff !important;
  box-shadow: none !important;
  font-size: 11px !important;
}

.settings-page .settings-form textarea.form-control {
  height: auto !important;
  min-height: 76px !important;
}

.settings-page .settings-form .form-control:focus {
  border-color: var(--settings-teal) !important;
  box-shadow: 0 0 0 3px rgba(30, 149, 143, 0.1) !important;
}

.settings-page .settings-form em.text-muted { font-size: 8px; font-weight: 500; }

.settings-page .settings-form fieldset .image-container {
  min-height: 132px;
  padding: 9px;
  border: 1px dashed #cfdae6;
  border-radius: 12px;
  background: #f8fafc;
}

.settings-page .settings-form fieldset .image-container > div {
  width: 118px !important;
  height: 118px !important;
  max-width: 100%;
}

.settings-page .settings-form fieldset .image-container img {
  max-width: 100%;
  max-height: 100%;
  border-radius: 9px;
  object-fit: contain;
}

.settings-page .settings-form .custom-file,
.settings-page .settings-form .custom-file-input,
.settings-page .settings-form .custom-file-label {
  height: 38px;
  min-height: 38px;
}

.settings-page .settings-form .custom-file-label {
  padding: 8px 10px;
  overflow: hidden;
  color: #657185;
  border-color: #dbe4ee;
  border-radius: 9px;
  background: #fff;
  font-size: 9px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.settings-page .settings-form .custom-control-label {
  color: #526174;
  font-size: 10px;
}

.settings-page .settings-form .custom-control-input:checked ~ .custom-control-label::before {
  border-color: var(--settings-teal);
  background: var(--settings-teal);
}

.settings-page .settings-form > form > .btn-primary:last-of-type {
  display: inline-flex;
  min-width: 120px;
  min-height: 38px;
  margin-top: 14px !important;
  padding: 8px 15px !important;
  align-items: center;
  justify-content: center;
  color: #fff !important;
  border: 0 !important;
  border-radius: 10px !important;
  background: linear-gradient(135deg, var(--settings-navy), var(--settings-blue), var(--settings-teal)) !important;
  box-shadow: 0 7px 16px rgba(20, 96, 143, 0.18) !important;
  font-size: 11px !important;
  font-weight: 700;
}

@media (max-width: 767.98px) {
  .settings-hero { align-items: flex-start; flex-direction: column; }
  .settings-page .settings-form-shell > .card-body { padding: 13px !important; }
  .settings-page .settings-form fieldset { padding: 12px !important; }

  .settings-page .settings-form fieldset > .row > .col-4 {
    flex: 0 0 100%;
    max-width: 100%;
    margin-bottom: 14px;
  }
}
</style>
